import logService from '../wss/logService3';

/**
 * YourNoteNodeContainer
 *
 * Stores paginated entries per node for the active TopicGroup in YourNotes.
 * Nodes are dynamic — keyed by nodeKey (e.g. 'g1', 'g2') as returned by the BE.
 * The number of nodes depends on the TopicGroup GroupBy setting (Importance, Priority, Stage).
 *
 * Responsibilities:
 * - Store entries + paging meta (total, page) per node
 * - Update or remove entries across all nodes on SignalR push
 * - Clear all nodes on group switch
 *
 * Not a CacheBase2 subclass — node structure differs from flat _items list.
 */

const _name = 'YourNoteNodeContainer';

class YourNoteNodeContainer {
    _nodes = {};

    setNode = (nodeKey, entries, total, page) => {
        try {
            this._nodes[nodeKey] = { entries, total, page };
        } catch (error) {
            logService.logError(`${_name}.setNode: ${error.message}`);
        }
    };

    getNode = (nodeKey) => {
        try {
            return this._nodes[nodeKey] ?? { entries: [], total: 0, page: 1 };
        } catch (error) {
            logService.logError(`${_name}.getNode: ${error.message}`);
            return { entries: [], total: 0, page: 1 };
        }
    };

    getNodes = () => {
        try {
            return { ...this._nodes };
        } catch (error) {
            logService.logError(`${_name}.getNodes: ${error.message}`);
            return {};
        }
    };

    clear = () => {
        try {
            this._nodes = {};
        } catch (error) {
            logService.logError(`${_name}.clear: ${error.message}`);
        }
    };

    /**
     * Updates matching entries across all nodes.
     * Returns true if mutated.
     */
    addOrUpdate = (data) => {
        try {
            let mutated = false;
            Object.keys(this._nodes).forEach((nodeKey) => {
                data.forEach((item) => {
                    const idx = this._nodes[nodeKey].entries.findIndex((e) => e.rk === item.rk);
                    if (idx === -1) return;
                    this._nodes[nodeKey].entries[idx] = item;
                    mutated = true;
                });
            });
            return mutated;
        } catch (error) {
            logService.logError(`${_name}.addOrUpdate: ${error.message}`);
            return false;
        }
    };

    /**
     * Removes entry from all nodes.
     * Returns true if mutated.
     */
    deleteEntry = (rk) => {
        try {
            let mutated = false;
            Object.keys(this._nodes).forEach((nodeKey) => {
                const idx = this._nodes[nodeKey].entries.findIndex((e) => e.rk === rk);
                if (idx === -1) return;
                this._nodes[nodeKey].entries.splice(idx, 1);
                this._nodes[nodeKey].total = Math.max(0, this._nodes[nodeKey].total - 1);
                mutated = true;
            });
            return mutated;
        } catch (error) {
            logService.logError(`${_name}.deleteEntry: ${error.message}`);
            return false;
        }
    };
}

const yourNoteNodeContainer = new YourNoteNodeContainer();
export default yourNoteNodeContainer;
